'use client';

import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSize } from 'components/Collections/hooks/useSize';
import {
  animate,
  useMotionValue,
  type ValueAnimationTransition,
} from 'framer-motion';
import { useTimeout } from 'hooks/useTimeout';
import styles from './Toast.module.css';
import { ToastAlert } from './ToastAlert';
import { ToastItemAnimationDisplay } from './ToastItemAnimationDisplay';
import { ToastItemContext } from './ToastItemContext';
import type {
  ToastItemInstance,
  ToastPlacement,
  UseToastAlertProps,
} from './ToastTypes';

export type ToastItemProps = {
  toast: ToastItemInstance;
  removeToast: (id: string) => void;
  placement?: ToastPlacement;
  precedingHeight?: number;
  registerHeight: (height: number) => void;
  isExiting?: boolean;
  onRemoveAnimationComplete: () => void;
  /**
   * The default close label for the toast used when the message prop is a string or a function
   * @note this can be overridden by the message prop when it is a plain object
   */
  defaultCloseLabel: string;
};

const EXIT_TIMEOUT: number | null = null;

/**
 * Handles Toast animations and mounting / unmounting, while
 * accepting either a string, render function, or react node
 * to render as its content.
 */
export function ToastItem({
  toast,
  precedingHeight,
  registerHeight,
  removeToast,
  placement = 'top',
  isExiting = false,
  onRemoveAnimationComplete,
  defaultCloseLabel,
}: ToastItemProps) {
  const { message, duration = EXIT_TIMEOUT, iteration } = toast;

  /**
   * Use a ref to get the height of the toast item for styling and positioning
   */
  const ref = useRef<HTMLDivElement>(null);
  useSize(ref, ({ height }) => registerHeight(height));

  // Tell our toast provider to close this toast
  const handleToastItemClose = useCallback(
    () => removeToast(toast.id),
    [removeToast, toast.id]
  );

  // Used to pass the duration state and dismiss handler to the toast alert
  const toastItemContextValue = useMemo(
    () => ({ duration, onDismiss: handleToastItemClose }),
    [duration, handleToastItemClose]
  );

  /**
   * Handle delaying closure of a toast when the user mouse cursor is interacting with the toast
   * Used to prevent the toast from closing when the user is reading the content
   */
  const [startTimeout, stopTimeout] = useTimeout(
    handleToastItemClose,
    duration
  );
  useEffect(() => {
    if (duration === null) return;
    startTimeout(duration);
  }, [duration, startTimeout, iteration]);

  // clear timeout when mouse enters and re-enable when mouse leaves
  const handleMouseEnter = useCallback(stopTimeout, [stopTimeout]);
  const handleMouseLeave = useCallback(() => {
    if (duration !== null) {
      startTimeout();
    }
  }, [duration, startTimeout]);

  /**
   * Handle dismissing a toast via swipe gesture
   * Used to enhance the user experience when dismissing a toast on touch devices
   */
  const [dismissedViaDrag, setDismissedViaDrag] = useState(false);
  // stateful dynamic value for toast drag animation
  const motionValueX = useMotionValue(0);

  // the rendered toast content based on the message prop
  const renderToast = useMemo(() => {
    /**
     * If the message is a function, call it with the callback function to close the toast and the default close label
     */
    if (typeof message === 'function') {
      return message(handleToastItemClose, defaultCloseLabel);
    }
    /**
     * If the message is a string, render a simple toast alert with the default close label
     */
    if (typeof message === 'string') {
      return (
        <ToastAlert
          closeLabel={defaultCloseLabel}
          onRequestClose={handleToastItemClose}
          title={message}
        />
      );
    }
    /**
     * If the message is a valid React element, return it
     */
    if (React.isValidElement(message)) {
      return message;
    }
    /**
     * If the message is a plain object, render a toast alert with the close label from the message prop
     */
    if (
      typeof message === 'object' &&
      message !== null &&
      message.constructor === Object
    ) {
      const { closeLabel = defaultCloseLabel, ...otherMessageProps } =
        message as UseToastAlertProps;

      return (
        <ToastAlert
          {...otherMessageProps}
          closeLabel={closeLabel}
          onRequestClose={handleToastItemClose}
        />
      );
    }
    return message as ReactNode;
  }, [message, handleToastItemClose, defaultCloseLabel]);

  const isTop = placement.indexOf('top') > -1;
  const multiplier = isTop ? 1 : -1;
  return (
    <ToastItemContext.Provider value={toastItemContextValue}>
      <div
        ref={ref}
        className={styles.toastItemWrapper}
        data-exiting={isExiting || undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          transform: `translateY(${(precedingHeight ?? 0) * multiplier}px)`,
        }}
      >
        <ToastItemAnimationDisplay
          isDismissed={dismissedViaDrag}
          isExiting={isExiting}
          isTop={isTop}
          onDragEnd={(_evt, { offset, velocity }) => {
            if (
              // if the user swipes to the left or right dismiss the toast
              Math.abs(velocity.x) > 50 ||
              Math.abs(offset.x) > (ref.current?.offsetWidth ?? 0) * 0.6
            ) {
              setDismissedViaDrag(true);
              handleToastItemClose();
            } else {
              // otherwise animate the toast back to its original position
              animate(motionValueX, 0, {
                ...INERTIA_TRANSITION,
                min: 0,
                max: 0,
              } satisfies ValueAnimationTransition);
            }
          }}
          onRemoveAnimationComplete={onRemoveAnimationComplete}
          style={{ x: motionValueX }}
        >
          {renderToast}
        </ToastItemAnimationDisplay>
      </div>
    </ToastItemContext.Provider>
  );
}

const INERTIA_TRANSITION = {
  type: 'inertia',
  bounceStiffness: 300,
  bounceDamping: 40,
  timeConstant: 300,
} satisfies ValueAnimationTransition;
